/**
 * User display-name resolver.
 *
 * The Settings → Profile tab writes `display_name` to Supabase auth's
 * `user_metadata` via `auth.updateUser({ data: { display_name } })`.
 * Older accounts may instead have `full_name`, `name`, or `first_name`
 * + `last_name` from OAuth providers or sign-up flows.
 *
 * This helper centralizes the fallback chain so every consumer agrees:
 *   1. user_metadata.display_name (set explicitly in Settings)
 *   2. user_metadata.full_name (OAuth providers like Google)
 *   3. user_metadata.name (manual signup pattern)
 *   4. user_metadata.first_name + last_name (split-name pattern)
 *   5. email local-part (legacy fallback — what most pages did before)
 *   6. "User" (ultimate fallback)
 *
 * Trim everything; treat empty strings as missing.
 */

type UserMetadataShape =
  | {
      display_name?: string | null;
      full_name?: string | null;
      name?: string | null;
      first_name?: string | null;
      last_name?: string | null;
    }
  | null
  | undefined;

type UserLike = {
  email?: string | null;
  user_metadata?: UserMetadataShape | Record<string, unknown> | null;
};

function nonEmpty(s: unknown): string | null {
  if (typeof s !== "string") return null;
  const trimmed = s.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function getUserDisplayName(user: UserLike | null | undefined): string {
  if (!user) return "User";
  const md = (user.user_metadata ?? {}) as UserMetadataShape & Record<string, unknown>;

  const display = nonEmpty(md?.display_name);
  if (display) return display;

  const full = nonEmpty(md?.full_name);
  if (full) return full;

  const name = nonEmpty(md?.name);
  if (name) return name;

  const first = nonEmpty(md?.first_name);
  const last = nonEmpty(md?.last_name);
  if (first || last) return [first, last].filter(Boolean).join(" ");

  const local = nonEmpty(user.email?.split("@")[0]);
  if (local) return local;

  return "User";
}
